import { PureComponent } from 'react';
import _ from 'lodash';
import { WebApps } from "@ModelViews/ModelViews";
import Board, { GetDefaultDashboardTheme } from "./Board";
import { BoardTheming } from "@UI/Dashboard/BoardTheming";
import { eIconType } from "@Primitive/SettingsListType";
import { ToolbarIconInfo, ToolbarIconInfo_ButtonRender } from "@Primitive/ToolbarIconButton";
import { fullscreenToggle } from "@Utils/Utils";
import { PWACanBeInstall, PWARequestUserInstall } from "./PWA";
import { LayoutMenuInfo } from "@UI/Layout/SimpleLayout";
import { StateStorage } from "@Utils/StateStorage";
import { getZDepthShadow } from "@Utils/Colors";
import { RenderToolbar } from "@UI/Layout/AdvanceLayout";
import { ToastContainer } from "react-toastify";
import { MacroUserValidationFormManager } from "@UI/Dashboard/BoardMacroUserValidationForm";
import { UserManager } from "@Utils/UserManager";
import { grey600 } from '@Utils/Colors';
// ------------------------------------------------------------------------------------------------------
export interface SingleBoardProps {
    width: number;
    height: number;
    domainInfo: WebApps.DomainInfo;
    userInfo: WebApps.UserInfoView;
    ServersInfo: { [key: number]: WebApps.CyanServerInfo };
    userSettings: WebApps.UserSettings;
}
// ------------------------------------------------------------------------------------------------------
const defaultDashboardTheme = GetDefaultDashboardTheme();
// ------------------------------------------------------------------------------------------------------
export default class SingleBoard extends PureComponent<SingleBoardProps>
{
    private pageId: string;
    // --------------------------------------------------------------------------------------------------
    constructor(props: SingleBoardProps) {
        super(props);
    }
    // ------------------------------------------------------
    onSelectedBoardOrPageChange = (boardId: string, pageId: string) => {
        this.pageId = pageId;
    }
    // ------------------------------------------------------
    private getRightMenu(userInfo: WebApps.UserInfoView, isOneBoardSolution: boolean = true): LayoutMenuInfo[] {
        // We are not login just have only the selection for logs
        if (userInfo == null) {
            return [];
        }

        let rightMenu: LayoutMenuInfo[] = [{
            primaryText: userInfo.Email,
            leftIcon: 'fas fa-user center'
        },
        { isDivider: true },
        (PWACanBeInstall()) ? {
            primaryText: 'Install App',
            leftIcon: 'fas fa-arrow-alt-to-bottom center',
            onClick: () => PWARequestUserInstall()
        } : null,
        {
            primaryText: 'Full Screen',
            leftIcon: 'fas fa-expand center',
            onClick: () => {
                try {
                    fullscreenToggle();
                } catch (ex) {
                    console.error(ex);
                }
            }
        },
        {
            primaryText: 'Clear Browsing Data',
            leftIcon: 'fas fa-trash center',
            onClick: () => {
                let storage = StateStorage.GetInstance();
                storage?.ClearAll();
                localStorage.clear();
            }
        },
        {
            primaryText: 'Reload App',
            leftIcon: 'fas fa-redo-alt center',
            onClick: () => {
                window.location.reload();
            }
        },
        { isDivider: true },
        {
            primaryText: 'Sign Out',
            leftIcon: 'fas fa-sign-out center',
            onClick: () => {
                if (process.env.GLOW_BUILD) {
                    var userManager = UserManager.GetInstance();
                    userManager?.logout();
                } else {
                    window.location.replace("../logout");
                }
            }
        }];

        return _.compact(rightMenu);
    }
    // --------------------------------------------------------------------------------------------------
    renderToolbarElements = (nodeKey: string) => {
        const boardTheme = BoardTheming.GetTheme(nodeKey);
        const rightMenu = this.getRightMenu(this.props.userInfo, this.props.domainInfo.IsOneBoardSolution);
        const appBarFontColor = boardTheme?.boardToolbar.style.fontColor ?? "white";
        const appBarHeight = boardTheme?.boardToolbar.height ?? 35;
        const iconMenu: ToolbarIconInfo[] = [{
            id: 'icon-menu',
            type: 'icon',
            icon: 'fas fa-user-circle center',
            iconType: eIconType.fontAwesome,
            style: {
                width: appBarHeight,
                height: appBarHeight,
                lineHeight: appBarHeight + 'px',
                color: appBarFontColor
            },
            onIconClickDropdown: true,
            priv: _.noop,
            nestedItems: _.map(rightMenu, (x, idx) => {
                return {
                    id: idx.toString(),
                    type: 'icon',
                    isSeparator: x.isDivider,
                    label: x.primaryText,
                    icon: x.leftIcon,
                    iconType: eIconType.fontAwesome,
                    iconColor: grey600,
                    priv: x.onClick
                } as ToolbarIconInfo;
            })
        }];

        return {
            LeftElements: [],
            CenterElements: [],
            RightElements: ToolbarIconInfo_ButtonRender(iconMenu, [], [], {},
                x => {
                    if (_.isFunction(x?.priv))
                        x.priv();
                })

        };
    };
    // --------------------------------------------------------------------------------------------------
    renderBoard(width: number, height: number) {
        var settings = this.props.domainInfo.PredefinedLoadingBoardInfo;
        return <Board
            key={"board-" + settings.BoardId}
            width={width}
            height={height}
            UserInfo={this.props.userInfo}
            DomainInfo={this.props.domainInfo}
            IsEditable={false}
            LoadingPublicBoard={null}
            SelectedPageId={this.pageId}
            onSelectedBoardOrPageChange={this.onSelectedBoardOrPageChange}
            Theme={defaultDashboardTheme}
            ServersInfo={this.props.ServersInfo}
            LoadingYodiBoardId={settings.BoardId}
            getToolbarElements={(settings.BoardSettings?.HideTopToolbar) ? this.renderToolbarElements : undefined}
        />;
    }
    // --------------------------------------------------------------------------------------------------
    render() {
        var settings = this.props.domainInfo.PredefinedLoadingBoardInfo;
        const boardTheme = BoardTheming.GetTheme(settings?.NodeKey);
        const appBarBackgroundColor = boardTheme?.appToolbar.style.backgroundColor;
        const appBarFontColor = boardTheme?.appToolbar.style.fontColor ?? "white";
        const appBarHeight = boardTheme?.appToolbar.height ?? 35;
        const appBarShadowLevel = boardTheme?.appToolbar.style.shadowLevel ?? 1;

        const toolbarElements = this.renderToolbarElements(settings?.NodeKey);
        let width = this.props.width;
        let height = this.props.height;
        if (!settings.BoardSettings?.HideTopToolbar)
            height -= appBarHeight;

        return <>
            <ToastContainer
                position={'top-right'}
                hideProgressBar={false}
                pauseOnHover={false}
                closeButton={false}
                closeOnClick
                draggable
                pauseOnFocusLoss
                newestOnTop
                style={{ paddingBottom: 8 }}
            />

            {settings.BoardSettings?.HideTopToolbar ? null :
                <RenderToolbar
                    height={appBarHeight}
                    style={{
                        zIndex: 100,
                        width: width,
                        color: appBarFontColor,
                        backgroundColor: appBarBackgroundColor,
                        boxShadow: getZDepthShadow(appBarShadowLevel)
                    }}
                    LeftElements={[<div
                        key='left'
                        style={{
                            paddingLeft: 10,
                            fontSize: 20,
                            height: appBarHeight,
                            lineHeight: appBarHeight + 'px',
                            color: appBarFontColor
                        }}>
                        {settings.BoardSettings?.BoardAppTitle}
                    </div>]}
                    RightElements={toolbarElements.RightElements}
                />}
            {this.renderBoard(width, height)}
            <MacroUserValidationFormManager />
        </>;
    }
    // --------------------------------------------------------------------------------------------------
}
// ------------------------------------------------------------------------------------------------------